import React, {useEffect, useRef, useState} from 'react'
import {Button, FileInput, Panel, Spinner} from 'nf-ui'
import {Profile} from './types'
import styled from 'styled-components'
import {usePanelNavigator} from '~/components/PanelNavigator'
import {PanelLayout} from '~/components/PanelLayout'
import {Image} from '~/components/ProfilePicture/ProfilePicture'
import {isIOS} from 'react-device-detect'
import {Column, Row} from '~/components/Primitives/Layout'
import {validImageMimeTypes} from '~/util/validImages'
import {useAlert} from 'react-alert'
import {useLinkPhoto, useUploadPhoto} from '~/apollo/queries/Photos'
import {PhotoCroppingState} from '../../PhotoCroppingModal'

const DEBUG = 0
const GRID_ABSOLUTE_X = 280
const PHOTO_HEIGHT = 273
const PHOTO_WIDTH = 251
const ROW_HEIGHT = 34
const ANCHOR_WIDTH = 32
const POPUP_PADDING = 20
const BUTTON_HEIGHT = 40
const PANEL_HEIGHT = PHOTO_HEIGHT + BUTTON_HEIGHT + POPUP_PADDING * 3
const PANEL_WIDTH = PHOTO_WIDTH + POPUP_PADDING * 2
const PANEL_HOVER_RETAIN_HEIGHT = (PANEL_HEIGHT - ROW_HEIGHT) / 2

const Anchor = styled.div`
    position: absolute;
    width: ${ANCHOR_WIDTH}px;
    height: ${ROW_HEIGHT}px;
    opacity: 0;
`

const Highlight = styled.div<{opacity: number; background: string}>`
    position: absolute;
    width: ${ANCHOR_WIDTH}px;
    height: ${ROW_HEIGHT}px;
    background: ${(props) => props.background};
    opacity: ${(props) => props.opacity};
`

const ManagePhotoImageContainer = styled.span`
    top: ${POPUP_PADDING}px;
    left: ${POPUP_PADDING}px;
    width: ${PHOTO_WIDTH}px;
    height: ${PHOTO_HEIGHT}px;
    position: absolute;
`

export interface ManagePhotoProps {
    hoverColumn: number
    hoverRow: number
    profiles: Profile[]
    profileIdStrs: string[]
    otherMouseGesture: {buttons: number; type: string} | undefined
    activeRowOverride: number
    getCellBounds: (col: number, row: number) => {x: number; y: number; width: number; height: number} | undefined
    organisationIdStr: string
    photoCropping: PhotoCroppingState
    setPhotoCropping: (state: PhotoCroppingState) => void
    fileInputRef: React.RefObject<HTMLInputElement>
    onKeyDown?: ((event: React.KeyboardEvent<HTMLInputElement>) => void) | undefined
    onKeyUp?: ((event: React.KeyboardEvent<HTMLInputElement>) => void) | undefined
}

export const ManagePhoto = (props: ManagePhotoProps) => {
    const buttonRef = useRef<HTMLDivElement>(null)
    const {openPanel, panelProps, closePanel} = usePanelNavigator('managePhoto')
    const [activeRow, setActiveRow] = useState<number>(
        props.activeRowOverride > -1 ? props.activeRowOverride : props.hoverRow,
    )
    const [uploadingPhoto, setUploadingPhoto] = useState(false)
    const fallbackUrl = require(`~/components/ProfilePicture/profile-fallback-${(activeRow + 1) % 6}.jpg`)
    const alert = useAlert()
    const [uploadPhoto] = useUploadPhoto(props.organisationIdStr)
    const [linkPhoto] = useLinkPhoto()

    const moveAnchor = () => {
        const anchorElement = document.getElementById('managePhotoAnchor')
        const highlightElement = document.getElementById('managePhotoHighlight')
        if (!anchorElement || !highlightElement) return
        if (activeRow < 0) {
            highlightElement.style['display'] = 'none'
            return
        }
        const bounds = props.getCellBounds(0, activeRow)
        if (!bounds) {
            highlightElement.style['display'] = 'none'
            return
        }
        const viewPortHeight = isIOS ? window.visualViewport!.height : window.innerHeight
        highlightElement.style['display'] = ''
        anchorElement.style['top'] = `${Math.min(bounds.y, viewPortHeight - PANEL_HOVER_RETAIN_HEIGHT - ROW_HEIGHT)}px`
        anchorElement.style['left'] = `${bounds.x + bounds.width - GRID_ABSOLUTE_X}px`
        highlightElement.style['top'] = `${bounds.y}px`
        highlightElement.style['left'] = `${bounds.x + bounds.width - GRID_ABSOLUTE_X}px`
    }

    const lastHoverRow = useRef(-1)
    const lastHoverColumn = useRef(-1)
    useEffect(() => {
        if (props.hoverRow === lastHoverRow.current && props.hoverColumn === lastHoverColumn.current) return
        lastHoverRow.current = props.hoverRow
        lastHoverColumn.current = props.hoverColumn
        // DEBUG && console.log('ManagePhoto useEffect [props.hoverRow, props.hoverColumn, panelProps]', [props.hoverRow, props.hoverColumn, panelProps])

        if (uploadingPhoto || props.photoCropping.selection.length || props.activeRowOverride > -1) return

        if (!panelProps.open && props.hoverColumn === 0) {
            if (props.hoverRow >= 0) {
                setActiveRow(props.hoverRow)
            }
        } else if (props.hoverRow !== activeRow && props.hoverColumn === 0) {
            closePanel()
            setTimeout(() => {
                setActiveRow(props.hoverRow)
            }, 1)
        } else if (panelProps.open && props.hoverColumn !== 0 && props.hoverRow !== -1) {
            const hoverBounds = props.getCellBounds(props.hoverColumn, props.hoverRow)
            const anchorBounds = props.getCellBounds(0, activeRow)
            if (
                hoverBounds &&
                anchorBounds &&
                (hoverBounds.x <= anchorBounds.x ||
                    hoverBounds.x >= anchorBounds.x + ANCHOR_WIDTH + PANEL_WIDTH ||
                    hoverBounds.y + hoverBounds.height <= anchorBounds.y - PANEL_HOVER_RETAIN_HEIGHT ||
                    hoverBounds.y >= anchorBounds.y + ROW_HEIGHT + PANEL_HOVER_RETAIN_HEIGHT)
            ) {
                setActiveRow(-1)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.hoverRow, props.hoverColumn])

    useEffect(() => {
        // DEBUG && console.log('ManagePhoto useEffect [props.activeRowOverride]', [props.activeRowOverride])
        if (activeRow > -1) {
            closePanel()
            setTimeout(() => {
                setActiveRow(props.activeRowOverride)
            }, 1)
        } else {
            setActiveRow(props.activeRowOverride)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.activeRowOverride])

    useEffect(() => {
        if (activeRow === -1) {
            moveAnchor()
            closePanel()
        } else {
            moveAnchor()
            openPanel('managePhoto', {itemId: `managePhoto`})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRow])

    const profileUrl = props.profiles?.[activeRow]?.cellValues?.[0]?.values?.[2]

    return (
        <>
            <Anchor id="managePhotoAnchor" ref={buttonRef}>
                <div></div>
            </Anchor>
            <Highlight
                id="managePhotoHighlight"
                opacity={uploadingPhoto ? 1 : 0.05}
                background={uploadingPhoto ? 'white' : 'black'}
            >
                <Column centerVertical height={`${ROW_HEIGHT}px`}>
                    <Row centerHorizontal>{uploadingPhoto ? <Spinner></Spinner> : null}</Row>
                </Column>
            </Highlight>
            <Panel
                targetRef={buttonRef}
                {...panelProps}
                onClose={() => setActiveRow(-1)}
                noChildScroll={true}
                placement="right"
                maxHeight={`${PANEL_HEIGHT}px`}
            >
                <PanelLayout key="managePhoto" width={PANEL_WIDTH}>
                    <Row>
                        <Column width={`${POPUP_PADDING}px`}></Column>
                        <Column width={`${PANEL_WIDTH}px`} height={`${PANEL_HEIGHT}px`}>
                            <Row height={`${POPUP_PADDING}px`}></Row>
                            <Row height={`${PHOTO_HEIGHT}px`} width={`${PHOTO_WIDTH}px`}>
                                <ManagePhotoImageContainer
                                    onClick={() => {
                                        setActiveRow(-1)
                                    }}
                                >
                                    {profileUrl ? (
                                        <Image url={profileUrl}></Image>
                                    ) : (
                                        <Image
                                            url={fallbackUrl}
                                            style={{opacity: 0.15, border: '3px dashed black'}}
                                        ></Image>
                                    )}
                                </ManagePhotoImageContainer>
                            </Row>
                            <Row grow={1} height={`${POPUP_PADDING}px`}></Row>
                            <Row>
                                <Column grow={1} shrink={1}></Column>
                                <FileInput
                                    ref={props.fileInputRef}
                                    buttonProps={{variant: 'primary', style: {flexShrink: 0}}}
                                    accept={validImageMimeTypes}
                                    onChange={async (event) => {
                                        try {
                                            if (!event.target.files) return
                                            setUploadingPhoto(true)
                                            const result = await uploadPhoto({
                                                variables: {
                                                    organisationIdStr: props.organisationIdStr,
                                                    imgFile: event.target.files[0],
                                                },
                                            })
                                            result.data?.uploadPhoto?.idStr &&
                                                (await linkPhoto({
                                                    variables: {
                                                        photoIdStr: result.data?.uploadPhoto?.idStr,
                                                        profileIdStr: props.profileIdStrs[activeRow],
                                                    },
                                                }))
                                        } catch (error) {
                                            alert.error(error.message)
                                        }
                                        setUploadingPhoto(false)
                                    }}
                                    onKeyDown={(ev) => {
                                        if (ev.key === 'Tab') {
                                            ev.preventDefault()
                                        }
                                        if (ev.key !== 'Enter') {
                                            props.onKeyDown?.(ev)
                                        }
                                    }}
                                    onKeyUp={(ev) => {
                                        if (ev.key === 'Tab') {
                                            ev.preventDefault()
                                        }
                                        if (ev.key !== 'Enter') {
                                            props.onKeyUp?.(ev)
                                        }
                                    }}
                                >
                                    Upload photo
                                </FileInput>
                                {profileUrl && <Column grow={1} shrink={1}></Column>}
                                {profileUrl && (
                                    <Button
                                        onClick={() => {
                                            const [photoIdStr, originalUrl] =
                                                props.profiles?.[activeRow]?.cellValues?.[0]?.values || []
                                            props.setPhotoCropping({
                                                currentIndex: 0,
                                                selection: [{photoIdStr, originalUrl}],
                                            })
                                            setActiveRow(-1)
                                        }}
                                    >
                                        Crop & Rotate
                                    </Button>
                                )}
                                <Column grow={1} shrink={1}></Column>
                            </Row>
                            <Row height={`${POPUP_PADDING}px`}></Row>
                        </Column>
                        <Column width={`${POPUP_PADDING}px`}></Column>
                    </Row>
                </PanelLayout>
            </Panel>
        </>
    )
}
